import React, {useContext, useEffect, useRef, useState} from "react";
import api from "./api";
import {AbilityContext, Can} from "./helpers/Can";
import {Button, Modal, Space, Input, Tooltip, notification, message} from "antd";
import {EditOutlined, LoadingOutlined} from "@ant-design/icons";

import ReactQuill from 'react-quill';

import SaveOutlined from "@ant-design/icons/lib/icons/SaveOutlined";

import {Parser} from 'html-to-react';
import {useTranslation} from "react-i18next";
import useCurrentUser from "@/components/helpers/useCurrentUser";
import {EDITOR_MODULES} from "./helpers/quillEditorModules";
import {contentLanguage} from './helpers/documentLanguage';

export default ({code}) => {
    const {i18n} = useTranslation();
    const [fetched, setFetched] = useState(false);
    const [content, setContent] = useState();
    const [editing, setEditing] = useState();
    const [saving, setSaving] = useState(false);
    const [language, setLanguage] = useState('en');
    const [draftLanguage, setDraftLanguage] = useState('en');
    const requestedLanguage = contentLanguage(i18n.resolvedLanguage || i18n.language);
    const currentCode = useRef(code);
    const loadedRequest = useRef();
    const currentEdit = useRef();
    const pendingSave = useRef();
    currentCode.current = code;
    useEffect(() => () => { currentEdit.current = null; pendingSave.current = null; }, []);

    useEffect(() => {
        if(editing?.code === code) return;
        if(editing) {
            currentEdit.current = null;
            pendingSave.current = null;
            setSaving(false);
            setEditing(false);
            return;
        }
        if(loadedRequest.current?.code === code && loadedRequest.current.language === requestedLanguage) return;
        let active = true;
        loadedRequest.current = null;
        setFetched(false);
        setContent(null);
        api(`/api/content/${code}?lang=${requestedLanguage}`).then(res => {
            if(!active) return;
            loadedRequest.current = {code, language: requestedLanguage};
            setFetched(true);
            setContent(res.data);
            setLanguage(contentLanguage(res.headers?.['content-language'] || requestedLanguage));
        });
        return () => { active = false; };
    }, [code, requestedLanguage, editing])

    const currValue = useRef()

    const handleChange = (newValue) => {
        currValue.current = newValue;
    }

    const save = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if(pendingSave.current || currentEdit.current !== editing || draftLanguage !== editing.language) return;
        const request = {session: editing};
        pendingSave.current = request;
        setSaving(true);
        api.put(`/api/contents/${code}`, {lang: editing.language, content: {text: currValue.current}}).then(res => {
            if(currentCode.current !== code || currentEdit.current !== editing) return;
            loadedRequest.current = {code, language: editing.language};
            currentEdit.current = null;
            setFetched(true);
            setLanguage(contentLanguage(res.headers?.['content-language'] || editing.language));
            setEditing(false);
            setContent(res.data);
            message.success('Content saved.')
        }).catch(() => {
            if(currentEdit.current === editing) message.error('Content could not be saved. Please try again.');
        }).finally(() => {
            if(pendingSave.current !== request) return;
            pendingSave.current = null;
            setSaving(false);
        })
    }

    const cancel = (e) => {
        e.preventDefault();
        e.stopPropagation();
        currentEdit.current = null;
        pendingSave.current = null;
        setSaving(false);
        setEditing(false)
    }

    const onClick = (e)=> {
        e.preventDefault();
        e.stopPropagation();
        currValue.current = content;
        const session = {code, language: requestedLanguage, sourceLanguage: language};
        setDraftLanguage(language);
        currentEdit.current = session;
        setEditing(session)
    }

    const ability = useContext(AbilityContext);
    const currentUser = useCurrentUser()
    const canEdit = ability.can('manage','Content') || currentUser?.super_admin

    return (
        <>
            {!editing && (
                <div className={'_info-content'} lang={language}>
                    {!fetched && <LoadingOutlined/>}

                    {(new Parser).parse(content)}

                    {canEdit && (
                        <Tooltip title={'Edit Content'}>
                            <Button
                                type={'link'}
                                size='small'
                                onClick={onClick}
                                aria-label="Edit Content"
                                lang="en"
                                disabled={!fetched}
                                style={{marginTop:'1em'}}
                            >
                                <EditOutlined/>
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}

            {editing && (
                <div>
                    {editing.sourceLanguage !== editing.language && <div lang="en" style={{marginBottom: '1em'}}>
                        <label>
                            Draft language{' '}
                            <select value={draftLanguage} disabled={saving} onChange={event => setDraftLanguage(event.target.value)}>
                                <option value="en" lang="en">English</option>
                                <option value="fr" lang="fr">Français</option>
                            </select>
                        </label>
                        <div>Saving to: <span lang={editing.language}>{editing.language === 'fr' ? 'Français' : 'English'}</span>.
                            {' '}Select that language when the draft is ready to save.</div>
                    </div>}
                    <div lang={draftLanguage}>
                        <ReactQuill
                            onChange={handleChange}
                            defaultValue={content}
                            theme="snow"
                            modules={EDITOR_MODULES}
                            preserveWhitespace
                            readOnly={saving}
                        />
                    </div>

                    <Space lang="en" direction={'horizontal'} style={{marginTop:'1em'}}>
                        <Button
                            icon={<SaveOutlined aria-hidden="true"/>}
                            ghost type={'primary'}
                            onClick={save}
                            loading={saving}
                            disabled={draftLanguage !== editing.language}
                            aria-disabled={saving || draftLanguage !== editing.language}
                            aria-label="Save"
                            aria-busy={saving}
                        >
                            Save
                        </Button>

                        <Button onClick={cancel}>Cancel</Button>
                    </Space>
                </div>
            )}

        </>
    )
}
